import React from 'react';
import { Edit, Trash2, Eye, Users, User, Star, Calendar, CheckCircle } from 'lucide-react';

const JuryCard = ({ jury, onEdit, onDelete, onViewDetails }) => {
  const getStatutColor = (statut) => {
    const colors = {
      'CONSTITUE': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'VALIDE': 'bg-blue-100 text-blue-800 border-blue-200', 
      'ACTIF': 'bg-green-100 text-green-800 border-green-200',
      'ARCHIVE': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[statut] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatutIcon = (statut) => {
    switch(statut) {
      case 'CONSTITUE': return <Calendar className="h-3 w-3" />;
      case 'VALIDE': return <CheckCircle className="h-3 w-3" />;
      case 'ACTIF': return <CheckCircle className="h-3 w-3" />;
      case 'ARCHIVE': return <Users className="h-3 w-3" />;
      default: return <Users className="h-3 w-3" />;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 overflow-hidden">
      <div className="p-6">
        {/* Header avec actions */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{jury.nom}</h3>
              <p className="text-sm text-gray-600">
                {jury.type_soutenance?.nom || 'Type non défini'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onViewDetails(jury)}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Voir les détails"
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              onClick={() => onEdit(jury)}
              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Modifier"
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              onClick={() => onDelete(jury)}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Supprimer"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Président */}
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <Star className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-medium text-gray-700">Président</span>
          </div>
          <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
            <p className="text-sm text-gray-900 font-medium">
              {jury.president_info?.nom || 'Non désigné'}
            </p>
            {jury.president_info?.type && (
              <p className="text-xs text-gray-600 mt-1">
                {jury.president_info.type === 'interne' ? 'Enseignant interne' : 'Enseignant externe'}
              </p>
            )}
          </div>
        </div>

        {/* Membres */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <User className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium text-gray-700">Membres</span>
            </div>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {jury.nombre_membres || 0} membre{(jury.nombre_membres || 0) > 1 ? 's' : ''}
            </span>
          </div>
          
          <div className="space-y-2">
            {jury.membres_jury && jury.membres_jury.length > 0 ? (
              <>
                {jury.membres_jury.slice(0, 2).map((membre, index) => (
                  <div key={index} className="bg-blue-50 border border-blue-200 p-2 rounded text-xs">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-900 font-medium">
                        {membre.enseignant_info?.nom || 'Nom non disponible'}
                      </span>
                      <span className="text-blue-600 font-medium bg-blue-100 px-2 py-1 rounded">
                        {membre.role}
                      </span>
                    </div>
                    <p className="text-gray-600 mt-1">
                      {membre.enseignant_info?.type === 'interne' ? 'Interne' : 'Externe'}
                    </p>
                  </div>
                ))}
                {jury.membres_jury.length > 2 && (
                  <div className="text-xs text-gray-500 text-center bg-gray-50 p-2 rounded">
                    +{jury.membres_jury.length - 2} autre{jury.membres_jury.length - 2 > 1 ? 's' : ''}...
                  </div>
                )}
              </>
            ) : (
              <p className="text-xs text-gray-500 italic text-center py-2">
                Aucun membre ajouté
              </p>
            )}
          </div>
        </div>

        {/* Statut et date */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className={`inline-flex items-center space-x-1 px-3 py-1 text-xs font-medium rounded-full border ${getStatutColor(jury.statut)}`}>
            {getStatutIcon(jury.statut)}
            <span>{jury.statut}</span>
          </div>
          <span className="text-xs text-gray-500">
            {new Date(jury.date_constitution).toLocaleDateString('fr-FR')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default JuryCard; 